import * as React from 'react'
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Grow from '@mui/material/Grow'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'

import { store } from '../../../../store/app/Store'
import { soldStatuses, leasedStatuses } from 'utils/constants'
const soldStatusesArray = [...soldStatuses, ...leasedStatuses]

const options = [
  'All',
  '1 day',
  '3 days',
  '7 days',
  '30 days',
  '90 days',
  '180 days',
  '1 Year Ago',
  '2 Years Ago',
  '3 Years Ago',
  '4 Years Ago',
  '5 Years Ago',
  '6 Years Ago',
  '7 Years Ago',
]
const values = [0, 1, 3, 7, 30, 90, 180, 360, 720, 1080, 1440, 1800, 2160, 2520]

export default function DaysSold() {
  const globalState = React.useContext(store)
  const { state, dispatch } = globalState

  const [open, setOpen] = React.useState(false)
  const anchorRef = React.useRef(null)

  const [active, setActive] = React.useState(
    Array.isArray(state?.searchParamsSold?.status)
      ? state?.searchParamsSold?.status?.includes?.('U')
      : false
  )

  const numberOfDays = () => {
    if (state.searchParamsSold.minSoldDate === 0) return 0

    if (!state.searchParamsSold.minSoldDate) return 0

    const date = new Date(state.searchParamsSold.minSoldDate)
    const today = new Date()
    const timeDiff = Math.abs(today.getTime() - date.getTime())
    const numOfDays = Math.ceil(timeDiff / (1000 * 3600 * 24))

    // for any number of days return the closest value in the values array
    const final = values.reduce((prev, curr) =>
      Math.abs(curr - numOfDays) < Math.abs(prev - numOfDays) ? curr : prev
    )

    // if final is out of bound, return ''
    if (final > values[values.length - 1]) return ''

    return final
  }

  const [daysSold, setDaysSold] = React.useState(numberOfDays())
  const [selectedIndex, setSelectedIndex] = React.useState(
    values.indexOf(daysSold) !== -1 ? values.indexOf(daysSold) : 0
  )

  const handleChangeDaysSold = (value) => {
    setDaysSold(values[value])

    // calculate the date from today minus the number of days
    const date = new Date()
    date.setDate(date.getDate() - values[value])

    // Only update if showSoldListings is active
    if (state.showSoldListings) {
      const updatedParams = {
        ...state.searchParamsSold,
        minSoldDate:
          Number(values[value]) <= 0 ? 0 : date.toISOString().split('T')[0],
      }

      dispatch({
        type: 'searchParamsSold',
        newVal: updatedParams,
      })
    }
  }

  const handleClick = () => {
    // This ensures at least one set of pins is always visible on the map.
    if (active && !state.showActiveListings) {
      console.warn('Cannot turn off active listings while active listings are also off.');
      return; // Exit the function, preventing any state changes.
    }

    const newActiveState = !active
    setActive(newActiveState)

    // Create a clean copy of searchParams
    let baseParams = { ...state.searchParams }

    if (newActiveState) {
      // Adding sold status
      // Remove active listings related params
      baseParams.minListDate = null
      baseParams.status = ['U']

      // Now prepare the sold parameters
      let soldParams = { ...baseParams }

      // Add sold statuses
      if (Array.isArray(soldParams.lastStatus)) {
        soldParams.lastStatus = [...soldParams.lastStatus, ...soldStatusesArray]
      } else {
        soldParams.lastStatus = [...soldStatusesArray]
      }

      // Apply the current days filter
      if (values[selectedIndex] > 0) {
        const date = new Date()
        date.setDate(date.getDate() - values[selectedIndex])
        soldParams.minSoldDate = date.toISOString().split('T')[0]
      } else {
        soldParams.minSoldDate = 0
      }

      // Update sold params
      dispatch({
        type: 'searchParamsSold',
        newVal: soldParams,
      })
    } else {
      // console.log('not newActiveState')
      // Clear the sold params
      dispatch({
        type: 'searchParamsSold',
        newVal: {},
      })
    }

    // Toggle the visibility of sold listings
    dispatch({
      type: 'showSoldListings',
      newVal: newActiveState,
    })
  }

  const handleMenuItemClick = (event, index) => {
    setSelectedIndex(index)
    handleChangeDaysSold(index)
    setOpen(false)
  }

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen)
  }

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return
    }

    setOpen(false)
  }

  return (
    <React.Fragment>
      <ButtonGroup
        ref={anchorRef}
        variant="outlined"
        aria-label="Days Sold"
        className="daysActiveGroup"
      >
        <Button
          onClick={handleClick}
          variant={active ? `contained` : `outlined`}
          sx={{
            fontWeight: `700`,
            background: active ? `#AA482F` : `#fff`,
            '&:hover': {
              background: active ? `#AA482F` : `#fff`,
            },
            color: active ? `#fff` : `#686868`,
          }}
        >
          {state?.searchParams?.type?.includes('sale') ? 'Sold' : 'Leased'}
        </Button>
        <Button
          size="small"
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-label="select days active filter"
          aria-haspopup="menu"
          onClick={handleToggle}
          sx={{
            color: `#686868`,
            // visibility: active ? 'visible' : 'hidden',
          }}
          // disabled={!active}
        >
          {options[selectedIndex]} <KeyboardArrowDownIcon />
        </Button>
      </ButtonGroup>
      <Popper
        sx={{
          zIndex: 1300,
        }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
            sx={{ marginTop: `13px` }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu" autoFocusItem>
                  {options.map((option, index) => (
                    <MenuItem
                      key={option}
                      selected={index === selectedIndex}
                      onClick={(event) => handleMenuItemClick(event, index)}
                    >
                      {option}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </React.Fragment>
  )
}
